import { useState, useRef, useEffect } from 'react';
import { showToast } from 'components/ui/toast';

// API Base URL - update this to match your backend
const API_BASE = "https://image-service.beleef.com.au/api/v1";
// const API_BASE = "http://localhost:8080/api/v1";

/**
 * Custom hook for managing multiple file downloads with progress tracking
 * @returns {Object} Download manager functions and state
 */
export const useDownloadManager = () => {
  const [downloads, setDownloads] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  
  const eventSourcesRef = useRef({});
  const downloadAttemptsRef = useRef({});
  const downloadQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);

  /**
   * Process download queue with staggering to prevent browser blocking
   */
  const processDownloadQueue = async () => {
    if (isProcessingQueueRef.current || downloadQueueRef.current.length === 0) {
      return;
    }

    isProcessingQueueRef.current = true;

    while (downloadQueueRef.current.length > 0) {
      const taskId = downloadQueueRef.current.shift();
      
      const timestamp = new Date().toISOString();
      console.log(`[${timestamp}] Processing download from queue: ${taskId}`);
      
      await attemptDownloadWithRetry(taskId);
      
      // Wait 1 second between downloads to prevent browser blocking
      if (downloadQueueRef.current.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    isProcessingQueueRef.current = false;
    setIsDownloading(false);
  };

  /**
   * Start monitoring download progress via SSE
   */
  const startProgressMonitoring = (taskId) => {
    if (eventSourcesRef.current[taskId]) {
      eventSourcesRef.current[taskId].close();
      delete eventSourcesRef.current[taskId];
    }

    const progressUrl = `${API_BASE}/download/progress/${taskId}`;
    const eventSource = new EventSource(progressUrl);

    let downloadInitiated = false;
    let progressReached100 = false;

    eventSourcesRef.current[taskId] = eventSource;

    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] Download started for task: ${taskId}`);

    eventSource.onmessage = (event) => {
      try {
        const progressData = JSON.parse(event.data);

        setDownloads((prev) =>
          prev.map((d) =>
            d.taskId === taskId
              ? {
                  ...d,
                  progress: progressData.progress,
                  message: progressData.message,
                  status:
                    progressData.progress >= 100
                      ? "processing"
                      : progressData.progress > 0
                      ? "processing"
                      : "queued",
                }
              : d
          )
        );

        if (progressData.progress >= 100 && !progressReached100) {
          progressReached100 = true;

          if (eventSourcesRef.current[taskId]) {
            eventSourcesRef.current[taskId].close();
            delete eventSourcesRef.current[taskId];
          }

          // Add to queue instead of immediate download
          if (!downloadInitiated) {
            downloadInitiated = true;
            downloadQueueRef.current.push(taskId);
            processDownloadQueue();
          }
        }
      } catch (err) {
        console.error("Error parsing progress data:", err);
      }
    };

    eventSource.onerror = (error) => {
      if (downloadInitiated || progressReached100) {
        return;
      }

      if (eventSource.readyState === EventSource.CLOSED) {
        setDownloads((prev) =>
          prev.map((d) => {
            if (d.taskId === taskId && d.status !== "completed") {
              if (d.progress >= 100 && !downloadInitiated) {
                downloadInitiated = true;
                downloadQueueRef.current.push(taskId);
                processDownloadQueue();
                return {
                  ...d,
                  status: "processing",
                  message: "Finalizing download...",
                };
              }
              return {
                ...d,
                status: "failed",
                message: "Connection closed unexpectedly",
              };
            }
            return d;
          })
        );
      } else {
        setDownloads((prev) =>
          prev.map((d) =>
            d.taskId === taskId && d.status !== "completed"
              ? { ...d, status: "failed", message: "Connection error" }
              : d
          )
        );
      }

      if (eventSourcesRef.current[taskId]) {
        eventSourcesRef.current[taskId].close();
        delete eventSourcesRef.current[taskId];
      }
    };
  };

  /**
   * Attempt download with retry logic
   */
  const attemptDownloadWithRetry = async (taskId) => {
    const downloadInfo = downloadAttemptsRef.current[taskId];
    if (!downloadInfo) {
      return;
    }

    const { attempts, maxAttempts, fileName } = downloadInfo;
    const currentAttempt = attempts + 1;

    if (currentAttempt > 1) {
      setDownloads((prev) =>
        prev.map((d) =>
          d.taskId === taskId
            ? {
                ...d,
                message: `Retrying download (${currentAttempt}/${maxAttempts})...`,
                status: "processing",
              }
            : d
        )
      );
    }

    try {
      const success = await downloadResult(taskId, fileName);

      if (success) {
        delete downloadAttemptsRef.current[taskId];
      } else {
        throw new Error("Download did not trigger");
      }
    } catch (error) {
      downloadAttemptsRef.current[taskId].attempts = currentAttempt;

      if (currentAttempt < maxAttempts) {
        const retryDelay = 1000 * Math.pow(2, currentAttempt - 1);

        setTimeout(() => {
          attemptDownloadWithRetry(taskId);
        }, retryDelay);
      } else {
        setDownloads((prev) =>
          prev.map((d) =>
            d.taskId === taskId
              ? {
                  ...d,
                  status: "failed",
                  message: `Download failed after ${maxAttempts} attempts. Please try again.`,
                }
              : d
          )
        );

        showToast(
          "error",
          `Download failed after ${maxAttempts} attempts. Please try again.`
        );

        delete downloadAttemptsRef.current[taskId];
      }
    }
  };

  /**
   * Trigger the actual file download
   */
  const downloadResult = async (taskId, fileName) => {
    return new Promise((resolve) => {
      try {
        const timestamp = new Date().toISOString();
        console.log(`[${timestamp}] Attempting download for task: ${taskId}`);
        
        const resultUrl = `${API_BASE}/download/result/${taskId}`;

        const a = document.createElement("a");
        a.href = resultUrl;
        a.download = fileName || `download-${taskId}.zip`;
        a.style.display = "none";
        document.body.appendChild(a);

        let downloadStarted = false;

        const clickHandler = () => {
          downloadStarted = true;
          const timestamp = new Date().toISOString();
          console.log(`[${timestamp}] Download clicked for task: ${taskId}`);
        };

        a.addEventListener("click", clickHandler);

        // Give browser time to register event listener
        setTimeout(() => {
          a.click();
          
          // Wait for click event to fire
          setTimeout(() => {
            a.removeEventListener("click", clickHandler);
            
            // Keep element longer to ensure download starts
            setTimeout(() => {
              if (document.body.contains(a)) {
                document.body.removeChild(a);
              }
            }, 1000);

            if (downloadStarted) {
              setDownloads((prev) =>
                prev.map((d) =>
                  d.taskId === taskId
                    ? {
                        ...d,
                        status: "completed",
                        progress: 100,
                        message: "Download complete",
                      }
                    : d
                )
              );

              setTimeout(() => {
                removeDownload(taskId);
              }, 5000);

              showToast("success", "Download started successfully");
              resolve(true);
            } else {
              const timestamp = new Date().toISOString();
              console.log(`[${timestamp}] Download click not registered for task: ${taskId}`);
              resolve(false);
            }
          }, 500);
        }, 100);

      } catch (err) {
        const timestamp = new Date().toISOString();
        console.error(`[${timestamp}] Download error for task ${taskId}:`, err);
        setDownloads((prev) =>
          prev.map((d) =>
            d.taskId === taskId
              ? { ...d, status: "failed", message: "Download failed" }
              : d
          )
        );
        resolve(false);
      }
    });
  };

  /**
   * Queue a new download
   * @param {Array} images - Array of image objects with url and name
   * @param {string} folderName - Name for the zip file
   */
  const queueDownload = async (images, folderName) => {
    if (!images?.length) {
      showToast("error", "No images to download");
      return;
    }

    if (isDownloading) {
      showToast("warning", "Please wait for the current download to complete");
      return;
    }

    const tempTaskId = `temp-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    try {
      setIsDownloading(true);
      
      const downloadItem = {
        taskId: tempTaskId,
        fileName: `${folderName}.zip`,
        status: "queued",
        progress: 0,
        message: "Queuing download...",
      };

      setDownloads((prev) => [...prev, downloadItem]);

      const payload = {
        images: images.map((img) => ({
          url: img.url,
          name: img.name,
        })),
      };

      const response = await fetch(`${API_BASE}/downloadImages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || "Failed to queue download");
      }

      const serverTaskId = result.taskId;

      downloadAttemptsRef.current[serverTaskId] = {
        attempts: 0,
        maxAttempts: 3,
        fileName: `${folderName}.zip`,
      };

      setDownloads((prev) =>
        prev.map((d) =>
          d.taskId === tempTaskId
            ? { ...d, taskId: serverTaskId, status: "queued" }
            : d
        )
      );

      startProgressMonitoring(serverTaskId);
    } catch (error) {
      console.error("Download queue error:", error);
      showToast("error", "Failed to start download");
      setDownloads((prev) =>
        prev.map((d) =>
          d.taskId === tempTaskId
            ? { ...d, status: "failed", message: error.message }
            : d
        )
      );
      setIsDownloading(false);
    }
  };

  /**
   * Remove a download from the list
   */
  const removeDownload = (taskId) => {
    setDownloads((prev) => prev.filter((d) => d.taskId !== taskId));

    if (eventSourcesRef.current[taskId]) {
      eventSourcesRef.current[taskId].close();
      delete eventSourcesRef.current[taskId];
    }

    if (downloadAttemptsRef.current[taskId]) {
      delete downloadAttemptsRef.current[taskId];
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(eventSourcesRef.current).forEach((es) => es.close());
      eventSourcesRef.current = {};
      downloadAttemptsRef.current = {};
      downloadQueueRef.current = [];
    };
  }, []);

  return {
    downloads,
    isDownloading,
    queueDownload,
    removeDownload,
  };
};